import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const milestones = [
  { year: '2024年1月', title: '网站成立', description: '应用安全指南正式上线，致力于普及应用安全知识。' },
  { year: '2024年3月', title: '内容扩展', description: '新增"替代方案推荐"板块，为用户提供安全可靠的软件选择。' },
  { year: '2024年6月', title: '用户突破', description: '月访问量突破10万，获得越来越多用户的认可和信赖。' },
  { year: '2024年8月', title: '全面升级', description: '网站内容全面升级，涵盖安全风险、使用教程、替代方案和行业动态四大板块。' },
];

const advantages = [
  { icon: 'ri-shield-check-line', title: '客观公正', description: '所有内容基于公开可查的安全研究报告和行业数据，不偏袒任何商业利益。' },
  { icon: 'ri-user-heart-line', title: '用户至上', description: '以保护用户设备与数据安全为最高使命，内容通俗易懂，面向普通用户。' },
  { icon: 'ri-timer-line', title: '持续更新', description: '紧跟行业动态，定期更新内容，确保信息的时效性和准确性。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9pmcg1n2trhb35gvhrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg =
          (parsed as Record<string, unknown> | null)?.['meta'] && typeof (parsed as Record<string, unknown>).meta === 'object'
            ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message || ((parsed as Record<string, unknown>).meta as Record<string, unknown>).detail
            : (parsed as Record<string, unknown> | null)?.message || responseText || '提交失败，请稍后重试';
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : '网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 应用安全指南',
            description: '了解应用安全指南的品牌故事、发展历程、核心优势及联系方式。',
            url: 'https://example.com/about',
            inLanguage: 'zh-Hans',
            isPartOf: {
              '@type': 'WebSite',
              name: '应用安全指南',
              url: 'https://example.com/',
            },
          }),
        }}
      />

      <Navbar />
      <div className="pt-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="py-12 md:py-16 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              应用安全指南是一个专注于普及应用安全知识的信息平台，
              致力于帮助用户了解破解版应用的真实风险，并提供安全可靠的正版替代方案。
            </p>
          </section>
        </div>
      </div>

      {/* Brand Story */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-bold text-foreground-950 mb-6 text-center">品牌故事</h2>
          <div className="bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              在互联网高度发达的今天，我们每天都会接触到各种各样的应用软件。然而，随之而来的安全威胁也日益严峻。
              我们注意到，很多用户出于经济考虑或信息不对称，选择下载和使用来源不明的"破解版"应用，
              却对这些行为背后的安全隐患知之甚少。
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              正是基于这一观察，<strong>应用安全指南</strong>应运而生。我们的创始团队由一群关注数字安全的
              技术从业者和内容创作者组成，我们希望通过通俗易懂的方式，帮助每一位普通用户了解应用安全知识，
              认识到破解版软件的真实危害，并找到安全可靠的替代方案。
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              我们的使命很简单：<strong>让每一位用户都能在数字世界中安全、安心地使用软件。</strong>
              我们相信，安全不是奢侈品，而是每个人的基本权利。
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-bold text-foreground-950 mb-10 text-center">发展历程</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 md:-translate-x-px"></div>
            <div className="flex flex-col gap-8">
              {milestones.map((m, index) => (
                <div
                  key={m.year}
                  className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:flex flex-1"></div>
                  <div className="absolute left-2.5 md:left-1/2 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1/2 mt-1.5 z-10"></div>
                  <div className="ml-10 md:ml-0 md:flex-1 bg-white rounded-lg p-5 border border-background-200/70">
                    <span className="text-xs text-primary-500 font-semibold">{m.year}</span>
                    <h4 className="text-base font-semibold text-foreground-950 mt-1">{m.title}</h4>
                    <p className="text-sm text-foreground-600 mt-1">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-bold text-foreground-950 mb-10 text-center">我们的优势</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {advantages.map((adv) => (
              <div key={adv.title} className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className={`${adv.icon} text-xl`}></i>
                </div>
                <h4 className="text-base font-semibold text-foreground-950 mb-2">{adv.title}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 bg-background-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-bold text-foreground-950 mb-3 text-center">联系我们</h2>
          <p className="text-sm text-foreground-600 text-center mb-8">
            如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们取得联系。
          </p>

          <form
            data-readdy-form
            id="contact-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg p-6 md:p-8 border border-background-200/70"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  您的姓名 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-background-50"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  电子邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-background-50"
                  placeholder="请输入您的电子邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-background-50"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-background-50 resize-none"
                  placeholder="请输入您想说的内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多输入500个字符</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white py-3 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2 inline-block"></i>
                    提交中...
                  </>
                ) : (
                  '提交留言'
                )}
              </button>
            </div>

            {formStatus === 'success' && (
              <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                <i className="ri-check-line mr-1"></i>
                感谢您的留言！我们会在收到后尽快回复您。
              </div>
            )}

            {formStatus === 'error' && formError && (
              <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-lg text-sm text-accent-700">
                <i className="ri-error-warning-line mr-1"></i>
                {formError}
              </div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
}